import React, { useEffect } from "react";
import { AxiosRequestConfig } from "axios";
import { useStore } from "../../../Store";

import PageLayout from "../../Controls/PageLayout";
import Helpers from "../../Functions/Helpers";
import {
  Button,
  VStack,
  Text,
  UnorderedList,
  ListItem,
  HStack,
  Spinner,
} from "@chakra-ui/react";
import { Result } from "../../Result/Result";
import { UnAuthorizedIcon } from "../../Result/UnAuthorized";
import { ErrorResult, PostErrorLog } from "../../Functions/ErrorLogApi";
import { useMessageParentOrRedirect } from "../../../utils/useMessageParentOrRedirect";
import { redirectParentPage } from "../../../utils/redirectParentPage";

const noDataholderCallbackValuesError =
  "Data Holder Error: CallBack values are missing.";

export default function DhAuthCallBack({
  errorResultProp,
  queryStringProp,
}: {
  errorResultProp?: ErrorResult;
  queryStringProp?: string;
}) {
  const {
    token,
    setLoading,
    sessionDetails,
    selectedDataHolder,
    selectedCustomer,
    isEmbedded,
    data,
  } = useStore();

  const [errorResult, setErrorResult] = React.useState<ErrorResult | null>(
    errorResultProp ?? null
  );
  const { messageParentOrRedirect } = useMessageParentOrRedirect();

  const queryString = window.location.search || window.location.hash;
  const queryParams =
    queryString.startsWith("?") || queryString.startsWith("#")
      ? queryString.substring(1)
      : queryString;

  const urlParams = new URLSearchParams(queryParams);

  const code = urlParams.get("code");
  let response = urlParams.get("response"); // this is used for JARM
  const idToken = urlParams.get("id_token");
  const state = urlParams.get("state");
  const cdrArrangementId = urlParams.get("cdr_arrangement_id");
  const error = urlParams.get("error");

  if (!response && !code && !queryStringProp) {
    // if we can't get a code or response from the url after a #, look for response after a ?
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    response = urlParams.get("response");
  }

  useEffect(() => {
    if (error != null) {
      let flow = sessionDetails?.flow;
      if (
        error === "access_denied" &&
        (flow === "consent" || flow === "direct-to-bank" || flow === "amend")
      ) {
        messageParentOrRedirect("CANCELLED", {
          message: `Cancelled at bank`,
          isEmbedded: isEmbedded,
          flow: sessionDetails?.flow!,
          consentId: data!.consent?.id,
          state: data!.session.state,
          redirectUriBase: data?.session.redirectUri!,
          sessionId: sessionDetails?.sessionId,
        });
        return;
      }

      handleError("Data Holder Error: " + error);
      return;
    }

    const missingDataToCallDataHolder =
      (code == null || idToken == null || state == null) && response == null;

    if (missingDataToCallDataHolder) {
      handleError(noDataholderCallbackValuesError);
      return;
    }

    let authObj: any = {
      code: code,
      response: response,
      id_token: idToken,
      state: state,
      cdrArrangementId: cdrArrangementId,
      sub: selectedCustomer?.dataholderBrandCustomerId ?? null, // Used for Sandbox to pass through the bank sub
    };

    let config: AxiosRequestConfig = {
      method: "post",
      url:
        process.env.REACT_APP_PUBLIC_API_BASE_URI +
        "/consent/" +
        sessionDetails?.consentId +
        "/dataHolderAuthResponse",
      headers: {
        Authorization: "Bearer " + token?.access_token,
      },
      data: authObj,
    };

    Helpers.CallApi(
      config,
      (response) => {
        messageParentOrRedirect("SUCCESS", {
          message: "Consent completed successfully",
          isEmbedded: isEmbedded,
          flow: sessionDetails?.flow!,
          consentId: data!.consent?.id,
          cdrAgreementId: response.data.cdrAgreementId,
          state: data!.session.state,
          redirectUriBase: data?.session.redirectUri!,
          sessionId: sessionDetails?.sessionId,
        });
      },
      (message, errorData: any) => {
        // handle error
        handleError(message, errorData);
      }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleError(message: string, errorApiData?: any) {
    setLoading(false);

    const errorData = {
      bankName: selectedDataHolder?.brand?.name ?? "a bank",
      sessionId: sessionDetails?.sessionId ?? "unknown",
      consentId: data?.consent?.id ?? "unknown",
      consentStartTime: data?.consent?.startTime ?? "unknown",
      timeUtcString:
        errorResult?.time?.toUTCString() ?? new Date().toUTCString(),
      currentUri: window.location.href,
    };

    PostErrorLog(errorData.sessionId, {
      consentId: errorData.consentId,
      userId: errorData.sessionId,
      bankName: errorData.bankName,
      errorTime: errorData.timeUtcString,
      consentStartTime: errorData.consentStartTime,
      currentUri: errorData.currentUri,
      message: `An error occured when receiving a call back from a DataHolder: ${message}`,
    });

    const messageToPutInErrorObject =
      errorApiData?.errors[0]?.message ?? message;

    const hasUserCancelled =
      errorApiData?.errors[0]?.code ===
      "urn:au-cds:error:cds-acsiss:Dataholder/UserCancelled";

    const hasUserNotAuthenticated =
      errorApiData?.errors[0]?.code ===
      "urn:au-cds:error:cds-acsiss:Dataholder/UserNotAuthenticated";

    if (message === noDataholderCallbackValuesError) {
      messageParentOrRedirect("ERROR", {
        message: noDataholderCallbackValuesError,
        isEmbedded: isEmbedded,
        flow: sessionDetails?.flow!,
        consentId: data!.consent?.id,
        state: data!.session.state,
        redirectUriBase: data?.session.redirectUri!,
        sessionId: sessionDetails?.sessionId,
      });
      return;
    }

    if (hasUserCancelled || hasUserNotAuthenticated) {
      messageParentOrRedirect(hasUserCancelled ? "CANCELLED" : "ERROR", {
        message: messageToPutInErrorObject,
        isEmbedded: isEmbedded,
        flow: sessionDetails?.flow!,
        consentId: data!.consent?.id,
        state: data!.session.state,
        redirectUriBase: data?.session.redirectUri!,
        sessionId: sessionDetails?.sessionId,
      });
      return;
    }

    // If we are using the direct-to-bank/amend flow, then we redirect the user with an error, instead of showing an error
    if (sessionDetails?.flow! === "direct-to-bank" || sessionDetails?.flow! === "amend") {
      messageParentOrRedirect("ERROR", {
        message: "Unexpected error occured",
        isEmbedded: isEmbedded,
        flow: sessionDetails?.flow!,
        consentId: data!.consent?.id,
        state: data!.session.state,
        redirectUriBase: data?.session.redirectUri!,
        sessionId: sessionDetails?.sessionId,
      });
      return;
    }

    // If some other error occurred, we'll show the error message to the user
    setErrorResult({
      message,
      time: new Date(),
      data: errorData,
    });
  }

  function GetEmailLink() {
    const subject = `SessionID: ${errorResult?.data?.sessionId} - Callback from ${errorResult?.data?.bankName} failed`;
    const body = `Redirect to ${errorResult?.data?.bankName} failed at ${errorResult?.data?.timeUtcString}
SessionID: ${errorResult?.data?.sessionId}
ConsentID: ${errorResult?.data?.consentId}
ConsentStartTime: ${errorResult?.data?.consentStartTime}
CurrentURI: ${errorResult?.data?.currentUri}
Error: ${errorResult?.message}
    `;

    return `mailto:help@acsiss.com.au?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  }

  function clickOkError() {
    messageParentOrRedirect("ERROR", {
      message: errorResult?.message ?? "Unknown Error",
      isEmbedded: isEmbedded,
      flow: sessionDetails?.flow!,
      consentId: data!.consent?.id,
      state: data!.session.state,
      redirectUriBase: data?.session.redirectUri!,
      sessionId: sessionDetails?.sessionId,
    });
  }

  if (errorResult) {
    return (
      <PageLayout>
        <Result
          icon={<UnAuthorizedIcon />}
          title="The consent failed"
          subtitle={"Sorry we couldn't create a consent with your bank."}
          extra={
            <>
              <VStack align="left" fontSize={14} py={8} gap={8}>
                <Text>
                  <strong>There's a few reasons why this might happen:</strong>
                  <UnorderedList>
                    <ListItem>A network error may have occurred.</ListItem>
                    <ListItem>
                      We might have encountered an error communicating with your
                      bank.
                    </ListItem>
                    <ListItem>
                      You may have cancelled the consent process with your bank,
                      or have taken too long completing the process.
                    </ListItem>
                  </UnorderedList>
                </Text>

                <Text>
                  <strong>What to do now?</strong>
                  <UnorderedList>
                    <ListItem>
                      Try again. Sometimes things go wrong, and it might work
                      the next time.
                    </ListItem>
                    <ListItem>
                      Click the 'Send Error Report' button below to send us an
                      email with the error details.
                    </ListItem>
                  </UnorderedList>
                </Text>
              </VStack>

              <HStack>
                <Button
                  onClick={() => {
                    redirectParentPage(GetEmailLink());
                  }}
                >
                  Send Error Report
                </Button>
                <Button colorScheme="blue" onClick={clickOkError} width={24}>
                  OK
                </Button>
              </HStack>
            </>
          }
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <VStack p={20} gap={8}>
        <Spinner size="xl" />
        <Text>Getting information from your bank.</Text>
      </VStack>
    </PageLayout>
  );
}
